import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import Monogram from "@/components/Monogram";
import api from "@/lib/api";

export default function JournalDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Article not found"))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-44 pb-32 text-center text-ivory-700" data-testid="journal-detail-loading">
        Loading…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="pt-44 pb-32 text-center text-ivory-700">
        <p className="font-display text-3xl mb-6">Article not found</p>
        <Link to="/journal" className="btn-ghost">Back to The Ivory Edit</Link>
      </div>
    );
  }
  const { article, related } = data;
  const paragraphs = (article.body || "").split(/\n\n+/);

  return (
    <>
      {/* Hero */}
      <section className="pt-32 lg:pt-40 bg-[#F5F2ED]">
        <div className="max-w-3xl mx-auto px-6 lg:px-0 text-center">
          <Reveal>
            <div className="eyebrow mb-5">{article.category} · {article.reading_time || 4} min read</div>
            <h1 className="font-display font-light text-ivory-800 text-5xl md:text-7xl leading-[1.02]">
              {article.title}
            </h1>
            {article.subtitle && (
              <p className="mt-6 font-display italic text-ivory-700 text-xl md:text-2xl leading-snug">
                {article.subtitle}
              </p>
            )}
          </Reveal>
        </div>
        <div className="mt-16 lg:mt-24 max-w-[1500px] mx-auto px-6 lg:px-14">
          <div className="zoom-wrap aspect-[16/10] lg:aspect-[16/8] overflow-hidden">
            <img src={article.hero_image} alt={article.title} className="zoom-img w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-[#F5F2ED] py-24 lg:py-32 px-6 lg:px-14 relative overflow-hidden">
        <Monogram size={520} opacity={0.045} color="#4A3E39" className="absolute -left-40 top-1/3 hidden md:block" />
        <div className="max-w-2xl mx-auto space-y-7 text-[17px] leading-[1.95] text-ivory-800">
          {paragraphs.slice(0, 3).map((p, i) => (
            <Reveal key={i} delay={i * 40}>
              <p>{p}</p>
            </Reveal>
          ))}
        </div>

        {article.pull_quote && (
          <Reveal>
            <div className="max-w-4xl mx-auto my-20 lg:my-28 text-center">
              <p className="font-display italic text-ivory-800 text-3xl md:text-5xl leading-[1.2]">
                &ldquo;{article.pull_quote}&rdquo;
              </p>
            </div>
          </Reveal>
        )}

        {article.secondary_image && (
          <div className="max-w-[1200px] mx-auto mb-20 lg:mb-28">
            <div className="zoom-wrap aspect-[16/9] overflow-hidden">
              <img src={article.secondary_image} alt="" className="zoom-img w-full h-full object-cover" />
            </div>
          </div>
        )}

        <div className="max-w-2xl mx-auto space-y-7 text-[17px] leading-[1.95] text-ivory-800">
          {paragraphs.slice(3).map((p, i) => (
            <Reveal key={i} delay={i * 40}>
              <p>{p}</p>
            </Reveal>
          ))}
        </div>
      </article>

      {/* Related */}
      {related && related.length > 0 && (
        <section className="bg-[#EEE7DE] py-24 lg:py-32 px-6 lg:px-14">
          <div className="max-w-[1500px] mx-auto">
            <div className="eyebrow mb-12">Continue reading</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
              {related.map((a) => (
                <Link key={a.id} to={`/journal/${a.slug}`} data-testid={`related-${a.slug}`} className="block group">
                  <div className="zoom-wrap aspect-[4/5] overflow-hidden">
                    <img src={a.hero_image} alt={a.title} className="zoom-img w-full h-full object-cover" />
                  </div>
                  <div className="mt-5">
                    <div className="eyebrow text-ivory-700">{a.category}</div>
                    <h3 className="mt-2 font-display text-ivory-800 text-2xl group-hover:text-[#6D1F2B] transition-colors">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
